import Button, { ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Loader from '@gym-atoms/Loader/Loader';
import { deactivateAccessCardForSiteLocation } from '@gym-graphql/mutations';
import { getAccessCardHistory } from '@gym-graphql/queries';
import { MemberAccessCard } from '@gym-particles/types/Member';
import { useAppSelector } from '@gym-redux/store';
import {
  AccessCardHistoryResponse,
  DeactivateAccessCardForSiteLocationMutationVariables,
  GetAccessCardHistoryQueryVariables
} from '@gym-src/API';
import { API } from 'aws-amplify';
import { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const AccessCardDeactivateModal = ({
  visible,
  onHideCallBack,
  member
}: AccessCardDeactivateModalProps) => {
  const { t } = useTranslation();
  const { gymId } = useParams<{ chainId: string; gymId: string }>();
  const currentUser = useAppSelector((state) => state.user.userId);

  const [dropdownError, setDropdownError] = useState<boolean>(false);
  const [selected, setselected] = useState('');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [noData, setNoData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [selectedCard, setSelectedCard] = useState<AccessCardHistoryResponse>();
  const [cardsDropdown, setCardsDropdown] = useState<SelectItem[]>();
  const [activeCards, setActiveCards] = useState<AccessCardHistoryResponse[]>();

  const getCards = async () => {
    setIsLoading(true);
    const data: GetAccessCardHistoryQueryVariables = {
      memberId: member.memberId,
      siteLocationId: +gymId
    };

    try {
      const result = await (API.graphql({
        query: getAccessCardHistory,
        variables: data
      }) as Promise<{
        data: { getAccessCardHistory: AccessCardHistoryResponse[] };
      }>);

      const cards: AccessCardHistoryResponse[] = [];
      const dropdownCards: SelectItem[] = [];

      const allCards = result.data.getAccessCardHistory;

      allCards.forEach((card) => {
        if (card.isActive === true) {
          cards.push(card);
          dropdownCards.push({
            label: card.accessCardUID?.toString() || '',
            value: card.accessCardUID?.toString() || ''
          });
        }
      });

      setActiveCards(cards);
      setCardsDropdown(dropdownCards);
    } catch (e) {
      console.log(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getCards();
  }, [visible]);

  useEffect(() => {
    if (!isLoading && cardsDropdown?.length === 0) {
      setNoData(true);
    } else {
      setNoData(false);
    }
  }, [cardsDropdown, isLoading]);

  const onDeactivateAccessCard = async () => {
    setIsDeactivating(true);
    setDropdownError(false);

    if (!selectedCard) {
      setDropdownError(true);
      setIsDeactivating(false);
      return;
    }

    try {
      const data: DeactivateAccessCardForSiteLocationMutationVariables = {
        accessCardUID: selectedCard.accessCardUID,
        memberId: member.memberId,
        lastModifiedBy: currentUser,
        siteLocationId: +gymId
      };
      const result = await (API.graphql({
        query: deactivateAccessCardForSiteLocation,
        variables: data
      }) as Promise<{
        data: {
          error?: boolean | null;
          errorType?: string | null;
          errorMsg?: string | null;
          statusCode?: string | null;
          statusMessage?: string | null;
        };
      }>);

      if (!result.data.error) {
        setErrorMsg(false);
      }
      hideModal();
    } catch (error) {
      console.error(error);
      setErrorMsg(true);
    } finally {
      setIsDeactivating(false);
    }
  };

  const cardChangeHandler = (e: DropdownChangeEvent) => {
    setselected(e.value);
  };

  useEffect(() => {
    setSelectedCard(activeCards?.find((card) => card.accessCardUID === selected));
  }, [activeCards, selected]);

  const dialogBoxContent = (
    <>
      {!isLoading && (
        <div>
          <label className="fw-bold">{t('DEACTIVATE_ACCESS_CARD.DROPDOWN_LABEL')}</label>

          <Dropdown
            onChange={cardChangeHandler}
            value={selected}
            options={cardsDropdown || []}
            error={dropdownError}
            placeholder={t('DEACTIVATE_ACCESS_CARD.DROPDOWN_PLACEHOLDER')}
          />
          <div className="mt15">
            {dropdownError && (
              <p className="p-error mb15">{t('DEACTIVATE_ACCESS_CARD.DROPDOWN_ERROR_TEXT')}</p>
            )}
          </div>
          {selected && selectedCard && (
            <p className="mt10">{`${t('DEACTIVATE_ACCESS_CARD.DEACTIVATE_MESSAGE')} ${
              selectedCard?.createdDate
            }`}</p>
          )}
          {errorMsg && (
            <p className="p-error mb15">{t('DEACTIVATE_ACCESS_CARD.DEACTIVATION_ERROR')}</p>
          )}
        </div>
      )}
      {isLoading && (
        <div>
          <div className="mb10">
            <Loader shape={'rectangle'} width={'quarter'} />
          </div>
          <div className="mb10">
            <Loader shape={'rectangle'} width={'full'} />
          </div>
          <div className="mb10">
            <Loader shape={'rectangle'} width={'full'} />
          </div>
          <div className="mb10">
            <Loader shape={'rectangle'} width={'full'} />
          </div>
        </div>
      )}
    </>
  );

  const hideModal = () => {
    setselected('');
    setDropdownError(false);
    setNoData(false);
    setIsDeactivating(false);
    setIsLoading(false);
    setSelectedCard(undefined);
    onHideCallBack();
  };

  return (
    <DialogBox
      variant={DialogBoxVariants.long}
      dialogVisible={visible}
      onHideCallback={() => hideModal()}
      dialogHeader={t('DEACTIVATE_ACCESS_CARD.TITLE')}
      dialogFooter={
        visible && !isLoading && !noData ? (
          <div>
            <Button
              label={t('DEACTIVATE_ACCESS_CARD.CANCEL_BUTTON')}
              variant={ButtonVariants.textonly}
              onClick={() => hideModal()}
            />
            <Button
              label={t('DEACTIVATE_ACCESS_CARD.DEACTIVATE_BUTTON')}
              icon={isDeactivating ? 'pi pi-spin pi-spinner' : ''}
              onClick={onDeactivateAccessCard}
              variant={ButtonVariants.danger}
            />
          </div>
        ) : visible && !isLoading && noData ? (
          <div>
            <Button
              label={t('DEACTIVATE_ACCESS_CARD.NO_CARD_OK_BUTTON')}
              onClick={() => hideModal()}
            />
          </div>
        ) : (
          <></>
        )
      }
      dialogDismissableMask={false}
      dialogClosable={true}
      content={
        visible && !noData ? (
          dialogBoxContent
        ) : visible && noData ? (
          <p>{t('DEACTIVATE_ACCESS_CARD.NO_CARD_ERROR_MESSAGE')}</p>
        ) : (
          <></>
        )
      }
    />
  );
};

export interface AccessCardDeactivateModalProps {
  visible: boolean;
  onHideCallBack: () => void;
  member: MemberAccessCard;
}

export default AccessCardDeactivateModal;
